/**
 * AppShell
 *
 * The main layout wrapper for all authenticated pages.
 * Sidebar on left, content on right.
 *
 * Usage:
 *   <AppShell>
 *     <YourPageContent />
 *   </AppShell>
 */

import { useState, useEffect } from 'react'
import Sidebar from './Sidebar'
import BottomTabBar from './BottomTabBar'
import GuestSignupGate from './GuestSignupGate'
import CreditsExhaustedGate from './CreditsExhaustedGate'
import { SidebarProvider, SIDEBAR_WIDTH } from '../context/SidebarContext'
import { IS_NATIVE_APP } from '../services/runtime'
import { PhotoCredit } from './PlayerAvatar'
import UpgradeBanner from './UpgradeBanner'

// Below this, there isn't room for the sidebar to sit next to content — it
// has to overlay instead of push. Matches Tailwind's `md` breakpoint, which
// the rest of the app's (currently nonexistent) responsive rules assume.
const MOBILE_BREAKPOINT = 768

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT

export default function AppShell({ children }) {
  const [isMobile, setIsMobile] = useState(isMobileViewport)
  // Starts closed on mobile (nothing to push content out of the way for),
  // open on desktop — same default as before there.
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobileViewport())

  useEffect(() => {
    const onResize = () => setIsMobile(isMobileViewport())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // On mobile the sidebar is a fixed-position overlay (see Sidebar.jsx) —
  // it floats on top of content rather than sharing the row with it, so
  // content never gets pushed down to a sliver-width column just because
  // someone opened the nav on their phone.
  const pushesContent = sidebarOpen && !isMobile

  // Broadcast the live offset as a CSS variable so fixed-position overlays
  // mounted OUTSIDE this AppShell's React tree (e.g. CookieConsent, which
  // sits at the App root so it can show on public pages too) can still
  // center against the content pane instead of the raw viewport. Context
  // alone can't reach them — they aren't nested under SidebarProvider.
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-offset', pushesContent ? `${SIDEBAR_WIDTH}px` : '0px')
    return () => document.documentElement.style.setProperty('--sidebar-offset', '0px')
  }, [pushesContent])

  return (
    <SidebarProvider value={{ open: pushesContent }}>
      {/* Native: an opaque strip behind the status bar / Dynamic Island. The
          app draws edge-to-edge (viewport-fit=cover) and <main> only pads its
          content down by the safe-area inset — so the moment a page scrolls
          (WebKit scrolls the focused Ask JAI composer into view when the
          keyboard opens, for one) the content slides up underneath the clock
          and notch and reads as broken. This keeps that band solid, the way
          a native nav bar would. pointer-events none so it never eats taps. */}
      {IS_NATIVE_APP && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 40,
            height: 'var(--safe-top)', background: '#FAFBFD', pointerEvents: 'none',
          }}
        />
      )}
      <div className="flex h-screen overflow-hidden" style={{ background: '#FAFBFD' }}>
        {/* On native, the sidebar (a desktop-style overlay with a hamburger)
            is replaced by a bottom tab bar — see BottomTabBar.jsx. Gated on
            IS_NATIVE_APP only tonight, not the mobile-web viewport too: that
            would be a visible change to the live product during draft week,
            which is Jasper's call, not this run's. */}
        {!IS_NATIVE_APP && <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen(v => !v)} />}
        <main
          className="flex-1 overflow-y-auto"
          style={{
            marginLeft: pushesContent ? SIDEBAR_WIDTH : 0,
            transition: 'margin-left 220ms cubic-bezier(0.4,0,0.2,1)',
            background: '#FAFBFD',
            // Safe-area padding so the notch/Dynamic Island never overlaps a
            // page's own header content, and the home indicator never sits
            // on top of the last row of a scrolled list. No-op on web — see
            // index.css. Left/right matters in landscape, where the notch
            // moves to a side edge. Bottom also clears the fixed tab bar
            // itself on native (48pt row + its own safe-bottom padding).
            paddingTop: 'var(--safe-top)',
            paddingBottom: IS_NATIVE_APP ? 'calc(var(--safe-bottom) + 48px)' : 'var(--safe-bottom)',
            paddingLeft: 'var(--safe-left)',
            paddingRight: 'var(--safe-right)',
          }}
        >
          {/* Upgrade banner: web only, behind a config flag (OFF by default). */}
          {!IS_NATIVE_APP && <UpgradeBanner />}
          {children}
          {/* Player headshots come from ESPN's public CDN (PlayerPhoto /
              PlayerAvatar, app-wide). One credit line per screen, here, so
              no page can show a photo without it. */}
          <PhotoCredit style={{ padding: '0 24px 16px' }} />
        </main>
        {IS_NATIVE_APP && <BottomTabBar />}
      </div>
      {/* Guest free-question wall — listens for the event api.js fires. */}
      <GuestSignupGate />
      <CreditsExhaustedGate />
    </SidebarProvider>
  )
}
